/*
Purpose: Download tutor timesheets as a PDF -- a summary table, then each timesheet in full
         (header + per-session breakdown + how the amount was worked out), one per page.
         Also the month dropdown helpers shared by the pages that use it.
Used by: ListTutorTimeSheet (one tutor) and Report-finance-timesheet (all tutors).
Project: TCMS
*/

import jsPDF from "jspdf";
import "jspdf-autotable";
import Moment from "moment";

const PAY_TYPE_LABELS = {
  1: "Flat Rate",
  2: "Per Session Rate",
  3: "Per Student Rate",
  4: "Banded Student Rate",
};

const HEAD_STYLES = { fillColor: [227, 230, 238], textColor: 20, fontStyle: "bold" };

const formatDate = (value) => (value ? Moment(value).format("DD-MM-YYYY") : "-");
const money = (value) => (Number(value) || 0).toFixed(2);
const payTypeLabel = (payType) => PAY_TYPE_LABELS[Number(payType)] || "-";
const statusLabel = (ts) => (Number(ts.timesheet_paid_status) === 1 ? "Paid" : "Unpaid");
const tutorName = (ts) =>
  [ts.tut_fname, ts.tut_surname].filter((part) => part && part !== "").join(" ") || "-";

const parseSessions = (ts) => {
  try {
    const sessions = JSON.parse(ts?.updated_timesheet_summary_json || "[]");
    return Array.isArray(sessions)
      ? [...sessions].sort((a, b) => String(a.session_date).localeCompare(String(b.session_date)))
      : [];
  } catch (e) {
    return [];
  }
};

// ---- Month dropdown helpers ----

// True when the timesheet's period overlaps the month ('YYYY-MM'), so a weekly timesheet that
// spans two months is listed under both. An empty month matches everything.
export const timesheetInMonth = (ts, month) => {
  if (!month) return true;
  const start = Moment(month, "YYYY-MM").startOf("month").format("YYYY-MM-DD");
  const end = Moment(month, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
  return ts.timesheet_from_date <= end && ts.timesheet_to_date >= start;
};

// Every month covered by the given timesheets, newest first, as { value: 'YYYY-MM', label: 'October 2026' }.
export const timesheetMonthOptions = (timesheets) => {
  const dates = (timesheets || [])
    .flatMap((ts) => [ts.timesheet_from_date, ts.timesheet_to_date])
    .filter(Boolean)
    .sort();
  if (dates.length === 0) return [];

  const options = [];
  const cursor = Moment(dates[dates.length - 1]).startOf("month");
  const first = Moment(dates[0]).startOf("month");
  while (!cursor.isBefore(first)) {
    options.push({ value: cursor.format("YYYY-MM"), label: cursor.format("MMMM YYYY") });
    cursor.subtract(1, "month");
  }
  return options;
};

// ---- PDF ----

// "How it was worked out" line. Pay type 3 is students x rate -- the total student count is
// already summed across sessions, so it is not multiplied by the session count again.
const formulaText = (ts, sessions) => {
  const rate = Number(ts.timesheet_pay_rate) || 0;
  const students = sessions.reduce((sum, s) => sum + (Number(s.no_of_students) || 0), 0);
  switch (Number(ts.timesheet_pay_type)) {
    case 1:
      return `Flat Rate = ${money(rate)}`;
    case 2:
      return `Per Session: ${sessions.length} sessions x ${money(rate)} = ${money(sessions.length * rate)}`;
    case 3:
      return `Per Student: ${students} students x ${money(rate)} = ${money(students * rate)}`;
    case 4:
      return `Banded Student Rate: sum of each session's band amount = ${money(
        sessions.reduce((sum, s) => sum + (Number(s.band_amount) || 0), 0)
      )}`;
    default:
      return "";
  }
};

const drawSummary = (doc, timesheets, currency, startY) => {
  const total = timesheets.reduce((sum, ts) => sum + (Number(ts.timesheet_total_amount) || 0), 0);
  doc.setFontSize(12);
  doc.setFont(undefined, "bold");
  doc.text("Summary", 14, startY);
  doc.setFont(undefined, "normal");

  doc.autoTable({
    startY: startY + 3,
    head: [["S.No", "Tutor", "Period", "Frequency", "Pay Type", "Sessions", "Students", "Adjustment", `Amount (${currency})`, "Status"]],
    body: timesheets.map((ts, i) => [
      i + 1,
      tutorName(ts),
      `${formatDate(ts.timesheet_from_date)} to ${formatDate(ts.timesheet_to_date)}`,
      ts.timesheet_frequency || "-",
      payTypeLabel(ts.timesheet_pay_type),
      ts.timesheet_no_of_sessions ?? 0,
      ts.timesheet_no_of_students ?? 0,
      money(ts.timesheet_adjustment_amount),
      money(ts.timesheet_total_amount),
      statusLabel(ts),
    ]),
    foot: [[{ content: "Total", colSpan: 8, styles: { halign: "right" } }, { content: money(total), styles: { halign: "right" } }, ""]],
    styles: { fontSize: 8, cellPadding: 1.5, valign: "top" },
    headStyles: HEAD_STYLES,
    footStyles: HEAD_STYLES,
    columnStyles: { 5: { halign: "right" }, 6: { halign: "right" }, 7: { halign: "right" }, 8: { halign: "right" } },
    didParseCell: (cell) => {
      if (cell.section === "body" && cell.column.index === 9) {
        cell.cell.styles.textColor = cell.cell.raw === "Paid" ? [25, 135, 84] : [220, 53, 69];
        cell.cell.styles.fontStyle = "bold";
      }
    },
  });
};

const drawTimesheet = (doc, ts, currency, top) => {
  const sessions = parseSessions(ts);
  const banded = Number(ts.timesheet_pay_type) === 4;
  const adjustment = Number(ts.timesheet_adjustment_amount) || 0;

  doc.setFontSize(13);
  doc.setFont(undefined, "bold");
  doc.text(tutorName(ts), 14, top);
  doc.setFont(undefined, "normal");

  doc.autoTable({
    startY: top + 4,
    body: [
      ["Period", `${formatDate(ts.timesheet_from_date)} to ${formatDate(ts.timesheet_to_date)}`, "Frequency", ts.timesheet_frequency || "-"],
      ["Pay Type", payTypeLabel(ts.timesheet_pay_type), "Pay Rate", banded ? "Banded" : `${money(ts.timesheet_pay_rate)} ${currency}`],
      ["Status", statusLabel(ts), "Paid Date", ts.timesheet_paid_date ? formatDate(ts.timesheet_paid_date) : "-"],
    ],
    theme: "grid",
    styles: { fontSize: 9, cellPadding: 1.8 },
    columnStyles: {
      0: { fontStyle: "bold", fillColor: HEAD_STYLES.fillColor, cellWidth: 28 },
      2: { fontStyle: "bold", fillColor: HEAD_STYLES.fillColor, cellWidth: 28 },
    },
  });

  let y = doc.lastAutoTable.finalY + 8;
  doc.setFontSize(11);
  doc.setFont(undefined, "bold");
  doc.text("Session Details", 14, y);
  doc.setFont(undefined, "normal");

  if (sessions.length) {
    doc.autoTable({
      startY: y + 3,
      head: [["S.No", "Date", "Name", "No. of Students", ...(banded ? ["Band", "Amount"] : [])]],
      body: sessions.map((s, i) => [
        i + 1,
        formatDate(s.session_date),
        s.session_name || "-",
        s.no_of_students ?? 0,
        ...(banded ? [s.band_label || "-", money(s.band_amount)] : []),
      ]),
      theme: "grid",
      styles: { fontSize: 9, cellPadding: 1.5 },
      headStyles: HEAD_STYLES,
      columnStyles: { 0: { cellWidth: 14 }, 1: { cellWidth: 26 }, 3: { halign: "right", cellWidth: 28 }, 5: { halign: "right" } },
    });
    y = doc.lastAutoTable.finalY;
  } else {
    doc.setFontSize(9);
    doc.text("No sessions in this timesheet.", 14, y + 8);
    y += 10;
  }

  // The totals block goes on the next page if it doesn't fit under the table.
  y += 8;
  if (y > doc.internal.pageSize.getHeight() - 30) {
    doc.addPage();
    y = 20;
  }
  doc.setFontSize(10);
  doc.text(formulaText(ts, sessions), 14, y);
  if (adjustment !== 0) {
    y += 6;
    doc.text(`Adjustment (deducted): ${money(adjustment)}`, 14, y);
  }
  y += 7;
  doc.setFont(undefined, "bold");
  doc.setFontSize(11);
  doc.text(`Final Amount = ${money(ts.timesheet_total_amount)} ${currency}`, 14, y);
  doc.setFont(undefined, "normal");
};

/**
 * Builds the timesheets PDF and downloads it.
 *
 * Rows need the tutor name fields and updated_timesheet_summary_json, as returned by
 * getTutorTimeSheetByTutor / getTutorTimesheetReportCenter.
 *
 * @param {object}   opts
 * @param {object[]} opts.timesheets
 * @param {string}   opts.title        e.g. "Timesheets - October 2026"
 * @param {string}  [opts.subtitle]    e.g. the tutor's name, or "All tutors"
 * @param {string}   opts.fileName     without the .pdf extension
 */
export const downloadTimesheetsPdf = ({ timesheets, title, subtitle = "", fileName }) => {
  const doc = new jsPDF({ format: "a4", orientation: "portrait" });
  const currency = sessionStorage.getItem("currency_code") || "";
  const centreName = sessionStorage.getItem("center_name") || "";

  // Title block on the first page
  doc.setFontSize(15);
  doc.setFont(undefined, "bold");
  doc.text(title, 14, 16);
  doc.setFont(undefined, "normal");
  doc.setFontSize(9);
  doc.setTextColor(90);
  doc.text([centreName, subtitle, `Printed ${Moment().format("DD-MM-YYYY")}`].filter(Boolean).join("  |  "), 14, 22);
  doc.setTextColor(0);

  drawSummary(doc, timesheets, currency, 32);

  // Each full timesheet starts on its own page.
  timesheets.forEach((ts) => {
    doc.addPage();
    drawTimesheet(doc, ts, currency, 18);
  });

  // Page numbers
  const pages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(120);
    doc.text(`Page ${p} of ${pages}`, doc.internal.pageSize.getWidth() - 14, doc.internal.pageSize.getHeight() - 8, { align: "right" });
    doc.setTextColor(0);
  }

  doc.save(`${fileName}.pdf`);
};
